import {
  Badge,
  Button,
  DateInput,
  EmptyState,
  FieldBlock,
  Input,
  LoadingState,
  Modal,
  Textarea,
  useToast,
} from '@orbind/ui'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import {
  ACTION_STATUS_META,
  MEETING_SECTION_ICON,
  MEETING_SECTION_LABEL,
  MEETING_STATUS_META,
  MOCK_MEETINGS,
  meetingDayKey,
  meetingDayLabel,
  meetingSection,
  type Meeting,
  type MeetingParticipant,
  type MeetingSection,
} from '../mocks/meetings.ts'
import '../features/work-item/wi.css'
import './meetings.css'

const STATUS_TONE = { 'ao-vivo': 'danger', agendada: 'info', encerrada: 'neutral' } as const
const ACTION_TONE = { pendente: 'warning', 'em-andamento': 'info', concluida: 'success' } as const

/** Duracoes oferecidas no modal (protótipo `_shell.html:4100`). */
const DURACOES = [15, 30, 45, 60] as const

/** Quem sou eu nos mocks — usado ao agendar e ao entrar numa reunião. */
const EU = { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você' } as const

const HORA_VALIDA = /^([01]\d|2[0-3]):[0-5]\d$/

const formatarData = (data: Date) =>
  `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}/${data.getFullYear()}`

/** "06/08/2026" → `Date`; devolve `null` se a string nao for uma data. */
const lerData = (rotulo: string): Date | null => {
  const [dia, mes, ano] = rotulo.split('/').map(Number)
  if (!dia || !mes || !ano) return null
  return new Date(ano, mes - 1, dia)
}

/** Copia para a area de transferencia sem quebrar quando a API nao existe. */
const copiar = async (texto: string) => {
  try {
    await navigator.clipboard?.writeText(texto)
  } catch {
    // ambiente sem permissao de clipboard — a mensagem de sucesso ja explica
    // o que foi copiado, e o protótipo tambem so notifica
  }
}

/** Linha da lista do explorer: cabeçalho de seção, de dia, ou reunião. */
type LinhaLista =
  | { kind: 'section'; id: string; label: string }
  | { kind: 'day'; id: string; label: string }
  | { kind: 'meeting'; id: string; meeting: Meeting; section: MeetingSection; meta: string }

/**
 * Reunioes — a agenda vive no EXPLORER DO SHELL (`shell/explorer.tsx`) e o
 * `<main>` alterna entre o detalhe da reunião (`showMeeting.html`) e o dock da
 * chamada (`showMeetDock.html`), que ocupa o `<main>` inteiro.
 *
 * Três modos convivem, como no protótipo (`_shell.html:3403-3409`):
 * - **dock** — a chamada ocupa o `<main>`; o detalhe não é desenhado;
 * - **tela cheia** (`meetFullOpen`, `_shell.html:1500-1550`) — sobreposição com
 *   "Adicionar pessoas" e "Copiar link";
 * - **minimizada** (`meetPill`) — a reunião continua viva numa pílula flutuante.
 *
 * Dados MOCADOS: entrar, encerrar, agendar e convidar mexem só no estado local.
 */
export function MeetingsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { search } = useExplorer()

  const [loading, setLoading] = useState(true)
  // copia local: agendar/encerrar/convidar mexem aqui, nunca no array do mock
  const [meetings, setMeetings] = useState<Meeting[]>(() => MOCK_MEETINGS.map((m) => ({ ...m })))
  // Reuniao ao vivo abre primeiro: e a unica que exige acao imediata.
  const [selectedId, setSelectedId] = useState<string | null>(
    MOCK_MEETINGS.find((m) => m.status === 'ao-vivo')?.id ?? null,
  )

  // sessao = a reuniao em que estou agora (protótipo: `st.meet`)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionVisible, setSessionVisible] = useState(true) // protótipo: `meetView`
  const [sessionMode, setSessionMode] = useState<'dock' | 'full'>('dock')
  const [micOn, setMicOn] = useState(true)
  const [camOn, setCamOn] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  // modal "Agendar reunião"
  const [schedOpen, setSchedOpen] = useState(false)
  const [schedTitle, setSchedTitle] = useState('')
  const [schedDate, setSchedDate] = useState<Date | null>(null)
  const [schedTime, setSchedTime] = useState('14:00')
  const [schedDuration, setSchedDuration] = useState<number>(30)
  const [schedAgenda, setSchedAgenda] = useState('')
  const [schedTouched, setSchedTouched] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 320)
    return () => clearTimeout(timer)
  }, [])

  const selected = meetings.find((m) => m.id === selectedId) ?? null
  const session = meetings.find((m) => m.id === sessionId) ?? null
  const onStage = session?.participants.filter((p) => p.joined) ?? []

  /* --------------------------------------------------------------- agenda */

  const abrirAgendar = (base: Meeting | null) => {
    setSchedTitle(base?.title ?? '')
    setSchedDate(base ? lerData(base.dateLabel) : new Date(2026, 7, 7))
    setSchedTime(base?.timeLabel ?? '14:00')
    setSchedDuration(30)
    setSchedAgenda('')
    setSchedTouched(false)
    setSchedOpen(true)
  }

  const erroTitulo = schedTitle.trim() === '' ? 'Informe o título da reunião.' : null
  const erroData = schedDate === null ? 'Escolha a data.' : null
  const erroHora = HORA_VALIDA.test(schedTime) ? null : 'Use o formato hh:mm (ex.: 14:00).'
  const agendarValido = erroTitulo === null && erroData === null && erroHora === null

  const agendar = () => {
    setSchedTouched(true)
    if (!agendarValido || schedDate === null) return
    const dateLabel = formatarData(schedDate)
    const nova: Meeting = {
      id: `mtg-${Date.now()}`,
      title: schedTitle.trim(),
      status: 'agendada',
      dateLabel,
      timeLabel: schedTime,
      whenLabel: `${dateLabel}, ${schedTime}`,
      durationLabel: `${schedDuration} min`,
      organizer: EU.name,
      channelId: null,
      channelName: null,
      contextCode: null,
      participants: [{ ...EU, joined: false, micOn: false, camOn: false }],
      agenda:
        schedAgenda.trim() === ''
          ? []
          : [{ id: `ag-${Date.now()}`, title: schedAgenda.trim(), owner: EU.name, minutes: schedDuration, done: false }],
      actions: [],
      notes: null,
    }
    setMeetings((atual) => [...atual, nova])
    setSelectedId(nova.id)
    setSchedOpen(false)
    toast.push({ tone: 'success', message: 'Reunião agendada. Convite enviado no canal e lembrete criado.' })
  }

  useExplorerConfig({
    footer: `${meetings.filter((m) => m.status === 'agendada').length} próximas · ${
      meetings.filter((m) => m.status === 'encerrada').length
    } realizadas`,
    onCreate: () => abrirAgendar(null),
  })

  /* ---------------------------------------------------------- a chamada -- */

  const entrar = (meeting: Meeting) => {
    setMeetings((atual) =>
      atual.map((m) =>
        m.id === meeting.id
          ? {
              ...m,
              status: 'ao-vivo',
              participants: m.participants.map((p) =>
                p.name === EU.name ? { ...p, joined: true, micOn: true } : p,
              ),
            }
          : m,
      ),
    )
    setSessionId(meeting.id)
    setSessionVisible(true)
    setSessionMode('dock')
    setAddOpen(false)
    setMicOn(true)
    setCamOn(false)
  }

  const encerrar = (meeting: Meeting) => {
    setMeetings((atual) =>
      atual.map((m) =>
        m.id === meeting.id
          ? {
              ...m,
              status: 'encerrada',
              notes: m.notes ?? 'Resumo registrado no canal ao encerrar a reunião.',
              participants: m.participants.map((p) => ({ ...p, joined: false, micOn: false, camOn: false })),
            }
          : m,
      ),
    )
    if (sessionId === meeting.id) {
      setSessionId(null)
      setAddOpen(false)
    }
    toast.push({
      tone: 'success',
      message: 'Reunião encerrada. Resumo registrado no canal e no histórico de reuniões.',
    })
  }

  /** Pessoas conhecidas dos mocks que ainda não estão nesta reunião. */
  const convidaveis = useMemo(() => {
    if (session === null) return []
    const jaEstao = new Set(session.participants.map((p) => p.name))
    const vistos = new Map<string, MeetingParticipant>()
    for (const meeting of MOCK_MEETINGS) {
      for (const pessoa of meeting.participants) {
        if (!jaEstao.has(pessoa.name) && !vistos.has(pessoa.name)) vistos.set(pessoa.name, pessoa)
      }
    }
    return [...vistos.values()]
  }, [session])

  const convidar = (pessoa: MeetingParticipant) => {
    if (session === null) return
    setMeetings((atual) =>
      atual.map((m) =>
        m.id === session.id
          ? {
              ...m,
              participants: [
                ...m.participants,
                { ...pessoa, id: `${pessoa.id}-${m.id}`, joined: true, micOn: false, camOn: false },
              ],
            }
          : m,
      ),
    )
    setAddOpen(false)
    toast.push({ tone: 'success', message: `Convite enviado para ${pessoa.name}.` })
  }

  const copiarLink = async (meeting: Meeting) => {
    await copiar(`${window.location.origin}/meetings/${meeting.id}`)
    toast.push({ tone: 'success', message: 'Link da reunião copiado. Qualquer pessoa interna pode entrar.' })
  }

  const copiarResumo = async (meeting: Meeting) => {
    await copiar(meeting.notes ?? `${meeting.title} — sem resumo registrado.`)
    toast.push({ tone: 'success', message: 'Resumo copiado para a área de transferência.' })
  }

  /* ------------------------------------------------- lista do explorer --- */

  const termo = search.trim().toLocaleLowerCase('pt-BR')
  const casa = (meeting: Meeting) =>
    termo === '' ||
    [meeting.title, meeting.organizer, meeting.channelName ?? '', meeting.dateLabel]
      .join(' ')
      .toLocaleLowerCase('pt-BR')
      .includes(termo)

  const visiveis = meetings.filter(casa)

  /**
   * Ao vivo (sem sub-cabeçalho) → Próximas (dias crescentes) → Anteriores
   * (dias decrescentes). Protótipo `_shell.html:3496-3542`.
   */
  const linhas = useMemo<LinhaLista[]>(() => {
    const saida: LinhaLista[] = []

    const metaDe = (meeting: Meeting, section: MeetingSection) => {
      if (section === 'ao-vivo') return `agora · ${meeting.participants.length} participantes`
      const canal = meeting.channelName ?? 'sem canal vinculado'
      return `${meeting.timeLabel} · ${meeting.durationLabel} · ${canal}`
    }

    const aoVivo = visiveis.filter((m) => m.status === 'ao-vivo')
    if (aoVivo.length > 0) {
      saida.push({ kind: 'section', id: 'sec-live', label: MEETING_SECTION_LABEL['ao-vivo'] })
      for (const meeting of aoVivo) {
        saida.push({ kind: 'meeting', id: meeting.id, meeting, section: 'ao-vivo', meta: metaDe(meeting, 'ao-vivo') })
      }
    }

    const porDia = (lista: Meeting[], section: MeetingSection, maisRecentesPrimeiro: boolean) => {
      saida.push({ kind: 'section', id: `sec-${section}`, label: MEETING_SECTION_LABEL[section] })
      const dias = new Map<string, Meeting[]>()
      for (const meeting of lista) {
        const atual = dias.get(meeting.dateLabel)
        if (atual) atual.push(meeting)
        else dias.set(meeting.dateLabel, [meeting])
      }
      const ordenados = [...dias.keys()].sort((a, b) =>
        maisRecentesPrimeiro
          ? meetingDayKey(b).localeCompare(meetingDayKey(a))
          : meetingDayKey(a).localeCompare(meetingDayKey(b)),
      )
      for (const dia of ordenados) {
        saida.push({ kind: 'day', id: `${section}-${dia}`, label: meetingDayLabel(dia) })
        for (const meeting of (dias.get(dia) ?? []).sort((a, b) => a.timeLabel.localeCompare(b.timeLabel))) {
          saida.push({ kind: 'meeting', id: meeting.id, meeting, section, meta: metaDe(meeting, section) })
        }
      }
    }

    porDia(
      visiveis.filter((m) => m.status === 'agendada'),
      'proximas',
      false,
    )
    porDia(
      visiveis.filter((m) => m.status === 'encerrada'),
      'anteriores',
      true,
    )

    return saida
  }, [visiveis])

  const lista: ReactNode = loading ? (
    <div style={{ padding: 14 }}>
      <LoadingState rows={5} label="Carregando reuniões" />
    </div>
  ) : visiveis.length === 0 ? (
    <div style={{ padding: '28px 16px' }}>
      <EmptyState
        icon="fas fa-video"
        title="Nenhuma reunião encontrada"
        description="Nenhuma reunião da agenda combina com a busca do explorer."
        action={
          <Button variant="secondary" icon="far fa-calendar-plus" onClick={() => abrirAgendar(null)}>
            Agendar reunião
          </Button>
        }
      />
    </div>
  ) : (
    linhas.map((linha) => {
      if (linha.kind === 'section') {
        return (
          <div key={linha.id} className="mt-section">
            {linha.label}
          </div>
        )
      }
      if (linha.kind === 'day') {
        return (
          <div key={linha.id} className="mt-day">
            {linha.label}
          </div>
        )
      }
      const { meeting, section } = linha
      return (
        <button
          key={meeting.id}
          type="button"
          className={`wi-row${meeting.id === selectedId ? ' wi-row--on' : ''}`}
          onClick={() => setSelectedId(meeting.id)}
        >
          <span
            className={`mt-rowicon${
              section === 'ao-vivo' ? ' mt-rowicon--live' : section === 'anteriores' ? ' mt-rowicon--done' : ''
            }`}
          >
            <i className={MEETING_SECTION_ICON[section]} aria-hidden style={{ fontSize: 13 }} />
          </span>
          <span className="wi-row__main">
            <span className="wi-row__top">
              <span className="wi-row__title">{meeting.title}</span>
            </span>
            <span className="wi-row__bottom">
              <span className="wi-row__code">{linha.meta}</span>
            </span>
          </span>
          {section === 'ao-vivo' ? (
            <span className="mt-rowlive">
              <i className="fas fa-circle" aria-hidden style={{ fontSize: 6 }} />
              Ao vivo
            </span>
          ) : null}
        </button>
      )
    })
  )

  /* ---------------------------------------------------------- a chamada -- */

  const tiles = (grande: boolean) => (
    <div className={`mt-tiles${grande ? ' mt-tiles--lg' : ''}`}>
      {onStage.map((person) => (
        <div key={person.id} className={`mt-tile${grande ? ' mt-tile--lg' : ''}`}>
          <span className="mt-tile__avatar">{person.initials}</span>
          <span className="mt-tile__name">{person.name}</span>
          <span
            className={`mt-tile__mic${person.micOn ? '' : ' mt-tile__mic--off'}`}
            title={person.micOn ? 'Microfone ligado' : 'Microfone desligado'}
            role="img"
            aria-label={`${person.name}: microfone ${person.micOn ? 'ligado' : 'desligado'}`}
          >
            <i className={person.micOn ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden />
          </span>
        </div>
      ))}
    </div>
  )

  const controles = (grande: boolean) =>
    session === null ? null : (
      <div className="mt-controls">
        <button
          type="button"
          className={`mt-ctrl${grande ? ' mt-ctrl--lg' : ''}${micOn ? '' : ' mt-ctrl--off'}`}
          aria-pressed={micOn}
          aria-label={micOn ? 'Desligar microfone' : 'Ligar microfone'}
          title={micOn ? 'Desligar microfone' : 'Ligar microfone'}
          onClick={() => setMicOn((on) => !on)}
        >
          <i className={micOn ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden style={{ fontSize: 15 }} />
        </button>
        <button
          type="button"
          className={`mt-ctrl${grande ? ' mt-ctrl--lg' : ''}${camOn ? '' : ' mt-ctrl--off'}`}
          aria-pressed={camOn}
          aria-label={camOn ? 'Desligar câmera' : 'Ligar câmera'}
          title={camOn ? 'Desligar câmera' : 'Ligar câmera'}
          onClick={() => setCamOn((on) => !on)}
        >
          <i className={camOn ? 'fas fa-video' : 'fas fa-video-slash'} aria-hidden style={{ fontSize: 15 }} />
        </button>
        <button
          type="button"
          className={`mt-ctrl${grande ? ' mt-ctrl--lg' : ''}`}
          aria-label="Compartilhar tela"
          title="Compartilhar tela"
          onClick={() =>
            toast.push({ tone: 'info', message: 'Compartilhamento de tela iniciado (simulado nesta prévia).' })
          }
        >
          <i className="fas fa-desktop" aria-hidden style={{ fontSize: 14 }} />
        </button>
        <button
          type="button"
          className={`mt-ctrl mt-ctrl--end${grande ? ' mt-ctrl--lg' : ''}`}
          aria-label="Encerrar reunião"
          title="Encerrar reunião"
          onClick={() => encerrar(session)}
        >
          <i className="fas fa-phone-slash" aria-hidden style={{ fontSize: 15 }} />
        </button>
      </div>
    )

  const cabecalhoAoVivo = (
    <>
      <span className="mt-dock__live">
        <span className="mt-dock__livedot" aria-hidden />
        AO VIVO
      </span>
      <span className="mt-dock__title">{session?.title}</span>
      <span className="mt-dock__count">
        {onStage.length} de {session?.participants.length ?? 0} na sala
      </span>
    </>
  )

  /** Dock: ocupa o `<main>` inteiro (protótipo `_shell.html:2834`). */
  const dock =
    session === null ? null : (
      <div className="mt-dock mt-dock--main">
        <div className="mt-dock__head">
          {cabecalhoAoVivo}
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="mt-dock__iconbtn"
            aria-label="Tela cheia"
            title="Usar a tela toda"
            onClick={() => setSessionMode('full')}
          >
            <i className="fas fa-expand" aria-hidden style={{ fontSize: 12 }} />
          </button>
          <button
            type="button"
            className="mt-dock__iconbtn"
            aria-label="Minimizar reunião"
            title="Minimizar — a reunião continua ativa"
            onClick={() => {
              setSessionVisible(false)
              setAddOpen(false)
            }}
          >
            <i className="fas fa-minus" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>
        {tiles(false)}
        {controles(false)}
      </div>
    )

  /** Tela cheia — protótipo `_shell.html:1500-1550` (`meetFullOpen`). */
  const telaCheia =
    session === null ? null : (
      <div className="mt-full" role="dialog" aria-modal="true" aria-label={`Reunião ${session.title} em tela cheia`}>
        <div className="mt-full__head">
          {cabecalhoAoVivo}
          <span style={{ flex: 1 }} />

          <div className="mt-addwrap">
            <button
              type="button"
              className="mt-full__btn"
              aria-expanded={addOpen}
              aria-haspopup="menu"
              onClick={() => setAddOpen((aberto) => !aberto)}
            >
              <i className="fas fa-user-plus" aria-hidden style={{ fontSize: 11 }} />
              Adicionar pessoas
            </button>
            {addOpen ? (
              <div className="mt-addmenu" role="menu" aria-label="Pessoas que podem ser convidadas">
                {convidaveis.length === 0 ? (
                  <p className="mt-addmenu__empty">Todas as pessoas já estão na reunião.</p>
                ) : (
                  convidaveis.map((pessoa) => (
                    <button
                      key={pessoa.name}
                      type="button"
                      role="menuitem"
                      className="mt-addmenu__item"
                      onClick={() => convidar(pessoa)}
                    >
                      <span className="mt-addmenu__avatar">{pessoa.initials}</span>
                      <span className="mt-addmenu__main">
                        <span className="mt-addmenu__name">{pessoa.name}</span>
                        <span className="mt-addmenu__role">{pessoa.role}</span>
                      </span>
                      <i className="fas fa-plus" aria-hidden style={{ fontSize: 10 }} />
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          <button type="button" className="mt-full__btn" onClick={() => void copiarLink(session)}>
            <i className="fas fa-link" aria-hidden style={{ fontSize: 11 }} />
            Copiar link da reunião
          </button>
          <button
            type="button"
            className="mt-dock__iconbtn"
            aria-label="Reduzir para a área de trabalho"
            title="Reduzir para a área de trabalho"
            onClick={() => {
              setSessionMode('dock')
              setAddOpen(false)
            }}
          >
            <i className="fas fa-compress" aria-hidden style={{ fontSize: 12 }} />
          </button>
          <button
            type="button"
            className="mt-dock__iconbtn"
            aria-label="Minimizar reunião"
            title="Minimizar — a reunião continua ativa"
            onClick={() => {
              setSessionVisible(false)
              setAddOpen(false)
            }}
          >
            <i className="fas fa-minus" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>
        {tiles(true)}
        {controles(true)}
      </div>
    )

  /** Pílula da reunião minimizada — protótipo `_shell.html:3405` (`meetPill`). */
  const pilula =
    session === null || sessionVisible ? null : (
      <div className="mt-pill">
        <button
          type="button"
          className="mt-pill__back"
          title="Voltar para a reunião em andamento"
          onClick={() => setSessionVisible(true)}
        >
          <span className="mt-pill__dot" aria-hidden />
          Reunião em andamento · {session.title}
        </button>
        <button type="button" className="mt-pill__end" title="Encerrar reunião" onClick={() => encerrar(session)}>
          <i className="fas fa-phone-slash" aria-hidden style={{ fontSize: 11 }} />
          Encerrar
        </button>
      </div>
    )

  /* -------------------------------------------------------- acoes do topo */

  /**
   * O par de ações depende do estado (protótipo `_shell.html:3562-3570`):
   * ao vivo → Voltar / Encerrar; agendada → Entrar agora / Remarcar;
   * encerrada → Ver gravação / Copiar resumo.
   */
  const acoesDoDetalhe = (meeting: Meeting) => {
    const naSessao = sessionId === meeting.id

    if (naSessao) {
      return (
        <>
          <Button icon="fas fa-video" onClick={() => setSessionVisible(true)}>
            Voltar para a reunião
          </Button>
          <Button variant="secondary" icon="fas fa-phone-slash" onClick={() => encerrar(meeting)}>
            Encerrar reunião
          </Button>
        </>
      )
    }

    if (meeting.status === 'encerrada') {
      return (
        <>
          <Button
            icon="fas fa-play"
            onClick={() =>
              toast.push({
                tone: 'info',
                message: 'Gravação disponível apenas com a integração de vídeo configurada.',
              })
            }
          >
            Ver gravação
          </Button>
          <Button variant="secondary" icon="far fa-copy" onClick={() => void copiarResumo(meeting)}>
            Copiar resumo
          </Button>
        </>
      )
    }

    return (
      <>
        <Button icon="fas fa-video" onClick={() => entrar(meeting)}>
          {meeting.status === 'ao-vivo' ? 'Entrar na reunião' : 'Entrar agora'}
        </Button>
        {meeting.status === 'ao-vivo' ? (
          <Button variant="secondary" icon="fas fa-phone-slash" onClick={() => encerrar(meeting)}>
            Encerrar reunião
          </Button>
        ) : (
          <Button variant="secondary" icon="far fa-calendar-alt" onClick={() => abrirAgendar(meeting)}>
            Remarcar
          </Button>
        )}
      </>
    )
  }

  /* --------------------------------------------------------------- modal  */

  const modalAgendar = (
    <Modal
      open={schedOpen}
      onClose={() => setSchedOpen(false)}
      title="Agendar reunião"
      width={480}
      footer={
        <>
          <Button variant="secondary" onClick={() => setSchedOpen(false)}>
            Cancelar
          </Button>
          <Button variant="primary" icon="far fa-calendar-plus" disabled={!agendarValido} onClick={agendar}>
            Agendar reunião
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FieldBlock label="Título" required error={schedTouched ? erroTitulo : null}>
          {({ id, invalid }) => (
            <Input
              id={id}
              invalid={invalid}
              value={schedTitle}
              placeholder="Ex.: Revisão da correção de CNPJ"
              onChange={(event) => setSchedTitle(event.target.value)}
              onBlur={() => setSchedTouched(true)}
            />
          )}
        </FieldBlock>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldBlock label="Data" required error={schedTouched ? erroData : null}>
            {({ invalid }) => <DateInput value={schedDate} onChange={setSchedDate} invalid={invalid} />}
          </FieldBlock>
          <FieldBlock label="Hora" required hint="Formato 24 horas" error={schedTouched ? erroHora : null}>
            {({ id, invalid }) => (
              <Input
                id={id}
                invalid={invalid}
                value={schedTime}
                inputMode="numeric"
                maxLength={5}
                placeholder="14:00"
                onChange={(event) => setSchedTime(event.target.value)}
                onBlur={() => setSchedTouched(true)}
              />
            )}
          </FieldBlock>
        </div>

        <div>
          <div className="mt-fieldlabel">Duração</div>
          <div className="mt-durs" role="group" aria-label="Duração da reunião">
            {DURACOES.map((minutos) => {
              const on = schedDuration === minutos
              return (
                <button
                  key={minutos}
                  type="button"
                  className={`mt-dur${on ? ' mt-dur--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => setSchedDuration(minutos)}
                >
                  {minutos} min
                </button>
              )
            })}
          </div>
        </div>

        <FieldBlock label="Pauta (opcional)">
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              value={schedAgenda}
              placeholder="O que será discutido?"
              onChange={(event) => setSchedAgenda(event.target.value)}
            />
          )}
        </FieldBlock>

        <p className="mt-schedhint">
          <i className="fas fa-bell" aria-hidden />
          Convite enviado no canal e lembrete 10 min antes pela central de notificações.
        </p>
      </div>
    </Modal>
  )

  /* -------------------------------------------------------------- render  */

  // Enquanto o dock ocupa o `<main>` o detalhe nao e desenhado — protótipo
  // `mainFree` (`_shell.html:2834`).
  const dockNoMain = session !== null && sessionVisible && sessionMode === 'dock'
  const telaCheiaAberta = session !== null && sessionVisible && sessionMode === 'full'

  return (
    <>
      <ExplorerBody>{lista}</ExplorerBody>

      {dockNoMain ? (
        dock
      ) : loading ? (
        <div style={{ padding: 32 }}>
          <LoadingState variant="detail" label="Carregando a reunião" />
        </div>
      ) : selected === null ? (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            icon="fas fa-video"
            title="Selecione uma reunião"
            description="Escolha uma reunião na agenda para ver participantes, pauta e ações combinadas."
            action={
              <Button icon="far fa-calendar-plus" onClick={() => abrirAgendar(null)}>
                Agendar reunião
              </Button>
            }
          />
        </div>
      ) : (
        <div className="mt-main">
          <div className="mt-stage">
            <div className="mt-lobby">
              <Badge tone={STATUS_TONE[selected.status]} icon={MEETING_STATUS_META[selected.status].icon}>
                {MEETING_STATUS_META[selected.status].label}
              </Badge>
              <h2 className="mt-lobby__title">{selected.title}</h2>
              <span className="mt-lobby__meta">
                {selected.whenLabel} · {selected.durationLabel} · organizada por {selected.organizer}
              </span>
              <span className="mt-lobby__meta">
                <i className="fas fa-hashtag" aria-hidden style={{ fontSize: 10, marginRight: 6 }} />
                {selected.channelName ?? 'Sem canal vinculado'}
              </span>
              <div className="mt-lobby__avatars">
                {selected.participants.map((person) => (
                  <span key={person.id} className="mt-person__avatar" title={person.name}>
                    {person.initials}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                {acoesDoDetalhe(selected)}
                {selected.channelId ? (
                  <Button
                    variant="ghost"
                    icon="fas fa-comments"
                    onClick={() => navigate(`/channels/${selected.channelId}`)}
                  >
                    Abrir canal
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-panel">
            <section className="mt-card">
              <div className="mt-card__head">
                <h3 className="mt-card__title">Participantes</h3>
                <span className="mt-card__count">{selected.participants.length}</span>
              </div>
              <div className="mt-list">
                {selected.participants.map((person) => (
                  <div key={person.id} className="mt-person">
                    <span className="mt-person__avatar">{person.initials}</span>
                    <span className="mt-person__main">
                      <span className="mt-person__name">{person.name}</span>
                      <span className="mt-person__role">
                        {person.role} · {person.joined ? 'na sala' : 'ainda não entrou'}
                      </span>
                    </span>
                    <span className="mt-person__mics">
                      <i
                        className={person.micOn ? 'fas fa-microphone' : 'fas fa-microphone-slash'}
                        aria-hidden
                        title={person.micOn ? 'Microfone ligado' : 'Microfone desligado'}
                      />
                      <i
                        className={person.camOn ? 'fas fa-video' : 'fas fa-video-slash'}
                        aria-hidden
                        title={person.camOn ? 'Câmera ligada' : 'Câmera desligada'}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-card">
              <div className="mt-card__head">
                <h3 className="mt-card__title">Pauta</h3>
                <span className="mt-card__count">
                  {selected.agenda.filter((item) => item.done).length}/{selected.agenda.length} concluídos
                </span>
              </div>
              {selected.agenda.length === 0 ? (
                <p className="mt-emptytext">Esta reunião ainda não tem pauta definida.</p>
              ) : (
                <div className="mt-list">
                  {selected.agenda.map((item) => (
                    <div key={item.id} className="mt-agenda">
                      <span
                        className={`mt-agenda__mark${item.done ? ' mt-agenda__mark--done' : ''}`}
                        role="img"
                        aria-label={item.done ? 'Item concluído' : 'Item pendente'}
                        title={item.done ? 'Item concluído' : 'Item pendente'}
                      >
                        <i className="fas fa-check" aria-hidden />
                      </span>
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span className={`mt-agenda__title${item.done ? ' mt-agenda__title--done' : ''}`}>
                          {item.title}
                        </span>
                        <span className="mt-agenda__meta">
                          {item.owner} · {item.minutes} min
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-card">
              <div className="mt-card__head">
                <h3 className="mt-card__title">Ações</h3>
                <span className="mt-card__count">{selected.actions.length}</span>
              </div>
              {selected.actions.length === 0 ? (
                <p className="mt-emptytext">
                  Nenhuma ação combinada até agora. As decisões viram Itens de Trabalho vinculados à reunião.
                </p>
              ) : (
                <div className="mt-list">
                  {selected.actions.map((action) => (
                    <div key={action.id} className="mt-action">
                      <span className="mt-action__main">
                        <span className="mt-action__title">{action.title}</span>
                        <span className="mt-action__meta">
                          {action.owner} · prazo {action.dueLabel}
                        </span>
                        <span>
                          <Badge tone={ACTION_TONE[action.status]} icon={ACTION_STATUS_META[action.status].icon}>
                            {ACTION_STATUS_META[action.status].label}
                          </Badge>
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {selected.notes ? (
              <section className="mt-card">
                <div className="mt-card__head">
                  <h3 className="mt-card__title">Resumo</h3>
                </div>
                <p className="mt-notes">{selected.notes}</p>
              </section>
            ) : null}
          </div>
        </div>
      )}

      {telaCheiaAberta ? telaCheia : null}
      {pilula}
      {modalAgendar}
    </>
  )
}
